import React, { useMemo } from 'react';
import { EventDtoV2 } from 'src/api/v2';
import { useTranslation } from 'react-i18next';

import { formatDateToNOString } from 'src/utils/FormatValue';
import { RegisterInkindModal } from 'src/features/inkind';
import { useBoolean } from 'usehooks-ts';
import AddEntityModalButton from 'src/components/AddEntityModalButton/AddEntityModalButton';
import { LoadingPage } from 'src/components/LoadingPage/LoadingPage';
import { Link } from 'react-router-dom';
import useEventDetails from 'src/hooks/use-event-details';
import { getSafeDateOnly } from 'src/utils/date';
import { EventFormWrapper } from 'src/features/projects';
import useProjects from 'src/features/projects/hooks/use-projects';
import EventCardStats from './EventCardStats';
import { Button, Card, Heading, Icon, Tag, type TagProps } from '@innovationnorway/cl-react';

type EventCardProps = {
  event: EventDtoV2;
  isFetching: boolean;
  projectId?: string;
};
const EventCard: React.FC<EventCardProps> = ({ event, isFetching, projectId }) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tEvents } = useTranslation('events');
  const { t: tSelfEffort } = useTranslation('selfEffort');
  const { eventDetails, isFetching: isFetchingStats } = useEventDetails(event.id);
  const { findProjectById, isFetching: projectsFetching } = useProjects();
  const project = useMemo(
    () => (projectsFetching ? undefined : findProjectById(event.projectId)),
    [findProjectById, projectsFetching, event.projectId],
  );
  const registerInKindModal = useBoolean();
  const utcCurrentDate = getSafeDateOnly(new Date());
  const utcStartDate = getSafeDateOnly(new Date(event.startDate));
  const utcEndDate = event.endDate ? getSafeDateOnly(new Date(event.endDate)) : undefined;
  const hasEventStarted: boolean = utcStartDate < utcCurrentDate;
  const hasEventEnded: boolean = utcEndDate ? utcEndDate < utcCurrentDate : utcStartDate < utcCurrentDate;
  const isEventActive: boolean = utcEndDate
    ? hasEventStarted && !hasEventEnded
    : utcCurrentDate.toString() == utcStartDate.toString();
  const eventStatus = useMemo(() => {
    if (hasEventStarted && hasEventEnded) {
      return 'completed';
    }
    if (!hasEventStarted) {
      return 'upcoming';
    }
    if (isEventActive) {
      return 'active';
    }
    return '';
  }, [hasEventStarted, hasEventEnded, isEventActive]);
  const tagType = useMemo<TagProps['data-color']>(() => {
    switch (eventStatus) {
      case 'completed':
        return 'success';
      case 'active':
        return 'info';
      case 'upcoming':
        return 'warning';
      default:
        return 'neutral';
    }
  }, [eventStatus]);
  const link = useMemo(
    () =>
      projectId ? `/projects/events/${event.id}?projectId=${projectId}` : `/projects/events/${event.id}`,
    [event.id, projectId],
  );
  return (
    <>
      {isFetching && projectsFetching ? (
        <LoadingPage />
      ) : (
        <Card className="margin-bottom--4">
          <Card.Block className="display--grid grid-cols--12 width--100 justify-content--space-between align-items--center">
            <div className="col-span--6">
              <div className="display--flex gap--1">
                <Tag data-color={tagType}>{tEvents(`${eventStatus}` as unknown as TemplateStringsArray)}</Tag>

                {project && <span className="font-size--smaller">{project.name}</span>}
              </div>

              <Link to={link}>
                <Heading level={2}>{event.name}</Heading>
              </Link>

              <span>
                {tCommon('startDate')}:{' '}
                {formatDateToNOString(event.startDate, false, '{date}.{month}.{year}', true)}
                {event.endDate && (
                  <>
                    {' '}
                    | {tCommon('endDate')}:{' '}
                    {formatDateToNOString(event.endDate, false, '{date}.{month}.{year}', true)}
                  </>
                )}
              </span>
            </div>

            <div className="col-span--3 display--flex justify-content--center">
              <EventCardStats
                event={event}
                eventDetails={eventDetails}
                eventStatus={eventStatus}
                isFetching={isFetchingStats}
              />
            </div>

            <div className="col-span--3 display--flex justify-content--flex-end align-items--center">
              <div className="display--flex flex-direction--column gap--2">
                <AddEntityModalButton
                  buttonVariant="secondary"
                  buttonText={
                    <>
                      <Icon prefix="fasr" iconName="edit" />
                      {tEvents('editEvent')}
                    </>
                  }
                  modalTitle={tEvents('editEvent')}
                >
                  <EventFormWrapper project={project} event={event} />
                </AddEntityModalButton>
                {eventStatus !== 'upcoming' && (
                  <Button type="button" variant="secondary" onClick={registerInKindModal.setTrue}>
                    <Icon prefix="fasr" iconName="sack-dollar" />
                    {tSelfEffort('registerSelfEffort')}
                  </Button>
                )}
              </div>
            </div>
          </Card.Block>
        </Card>
      )}
      {project?.id && (
        <RegisterInkindModal
          show={registerInKindModal.value}
          onClose={registerInKindModal.setFalse}
          defaultValues={{ projectId: project.id, eventId: event.id }}
        />
      )}
    </>
  );
};
export default EventCard;
